import React from 'react';

const DataTable = ({
  selectedTable,
  tableData,
  dataLoading,
  dataError,
  searchInput,
  searchTerm,
  onSearchInputChange,
  onSearch,
  onClearSearch,
  currentPage,
  perPage,
  totalPages,
  totalRecords,
  sortColumn,
  sortOrder,
  onSort,
  onPageChange,
  onPerPageChange,
  onEditClick,
  onDeleteClick,
  onDeleteTable,
  onAddRecord,
  onDeleteColumn,
  onAddColumn
}) => {
  // Helper function to infer field type from value
  const inferFieldType = (key, value) => {
    if (key.toLowerCase().includes('date') || key.toLowerCase().includes('time')) {
      return 'date';
    }
    if (typeof value === 'number') {
      return 'number';
    }
    if (typeof value === 'string' && value.length > 100) {
      return 'textarea';
    }
    return 'text';
  };

  return (
    <div className="column" style={{ overflow: 'hidden' }}>
      <div className="content" style={{ width: '100%', overflow: 'hidden' }}>
        {!selectedTable ? (
          <>
            <h1 className="title">Admin Dashboard</h1>
            <p>Select a table from the left panel to view details.</p>
          </>
        ) : (
          <>
            <div className="level">
              <div className="level-left">
                <div className="level-item">
                  <h1 className="title">{selectedTable}</h1>
                </div>
                <div className="level-item">
                  <button
                    className="button is-success is-small"
                    title="Add Record"
                    onClick={() => onAddRecord && onAddRecord(selectedTable)}
                  >
                    <span className="icon">
                      <i className="bi bi-plus"></i>
                    </span>
                    <span>Add Record</span>
                  </button>
                </div>
                <div className="level-item">
                  <button
                    className="button is-info is-small"
                    title="Add Column"
                    onClick={() => onAddColumn && onAddColumn(selectedTable)}
                  >
                    <span className="icon">
                      <i className="bi bi-plus-square"></i>
                    </span>
                    <span>Add Column</span>
                  </button>
                </div>
                <div className="level-item">
                  <button
                    className="button is-danger is-small"
                    title="Delete Table"
                    onClick={() => onDeleteTable && onDeleteTable(selectedTable)}
                  >
                    <span className="icon">
                      <i className="bi bi-trash"></i>
                    </span>
                    <span>Delete Table</span>
                  </button>
                </div>
              </div>
              <div className="level-right">
                <div className="level-item">
                  <form onSubmit={onSearch}>
                    <div className="field has-addons">
                      <div className="control">
                        <input
                          className="input"
                          type="text"
                          placeholder="Search..."
                          value={searchInput}
                          onChange={onSearchInputChange}
                        />
                      </div>
                      <div className="control">
                        <button className="button is-info" type="submit">
                          <span className="icon">
                            <i className="bi bi-search"></i>
                          </span>
                        </button>
                      </div>
                      {searchTerm && (
                        <div className="control">
                          <button
                            className="button is-light"
                            type="button"
                            onClick={onClearSearch}
                            title="Clear search"
                          >
                            <span className="icon">
                              <i className="bi bi-x-lg"></i>
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
            {dataLoading && (
              <div className="notification is-info is-light">
                <button className="button is-loading is-static">Loading table data...</button>
              </div>
            )}
            {dataError && (
              <div className="notification is-danger is-light">
                Error: {dataError}
              </div>
            )}
            {!dataLoading && !dataError && tableData.length > 0 && (
              <>
                <div
                  className="table-container"
                  style={{
                    overflowX: 'auto',
                    width: '100%',
                    maxWidth: '100%',
                    border: '1px solid #dbdbdb',
                    borderRadius: '4px'
                  }}
                >
                  <table
                    className="table is-striped is-hoverable"
                    style={{
                      minWidth: '1200px',
                      width: 'auto',
                      margin: 0,
                      tableLayout: 'auto'
                    }}
                  >
                    <thead>
                      <tr>
                        {Object.keys(tableData[0]).map((key) => (
                          <th key={key} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            <div className="is-flex is-align-items-center is-justify-content-space-between">
                              <div
                                className="is-flex is-align-items-center"
                                onClick={() => onSort(key)}
                                style={{ flex: 1 }}
                              >
                                <span>{key}</span>
                                <div className="is-flex is-flex-direction-column ml-2">
                                  <i
                                    className={`bi bi-caret-up-fill ${sortColumn === key && sortOrder === 'asc' ? 'has-text-primary' : 'has-text-grey-light'}`}
                                    style={{ fontSize: '0.7rem', lineHeight: '0.5rem' }}
                                  ></i>
                                  <i
                                    className={`bi bi-caret-down-fill ${sortColumn === key && sortOrder === 'desc' ? 'has-text-primary' : 'has-text-grey-light'}`}
                                    style={{ fontSize: '0.7rem', lineHeight: '0.5rem' }}
                                  ></i>
                                </div>
                              </div>
                              {/* Skip system fields for deletion */}
                              {key !== 'id' && !key.includes('_at') && (
                                <button
                                  className="button is-danger is-small ml-2"
                                  title={`Delete column ${key}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDeleteColumn && onDeleteColumn(selectedTable, key);
                                  }}
                                  style={{ padding: '2px 6px', fontSize: '0.7rem' }}
                                >
                                  <span className="icon is-small">
                                    <i className="bi bi-x"></i>
                                  </span>
                                </button>
                              )}
                            </div>
                          </th>
                        ))}
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((row, index) => (
                        <tr key={index}>
                          {Object.values(row).map((value, cellIndex) => {
                            // Check if this column has long content (250+ characters)
                            const hasLongContent = tableData.some(dataRow => {
                              const cellValue = Object.values(dataRow)[cellIndex];
                              return cellValue && String(cellValue).length > 250;
                            });

                            return (
                              <td key={cellIndex} style={{
                                maxWidth: hasLongContent ? '750px' : '250px',
                                minWidth: hasLongContent ? '400px' : '120px',
                                width: hasLongContent ? '750px' : 'auto',
                                wordWrap: 'break-word',
                                whiteSpace: 'normal',
                                overflow: 'hidden'
                              }}>
                                {value !== null && value !== undefined ? String(value) : ''}
                              </td>
                            );
                          })}
                          <td style={{ whiteSpace: 'nowrap', minWidth: '120px' }}>
                            <div className="buttons are-small">
                              <button
                                type="button"
                                className="backup-icon-button"
                                title="Edit"
                                onClick={() => onEditClick(row.id, row)}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                type="button"
                                className="backup-icon-button"
                                title="Delete"
                                onClick={() => onDeleteClick(row.id, row)}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                <div className="level">
                  <div className="level-left">
                    <div className="level-item">
                      <p className="subtitle is-6">
                        Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalRecords)} of {totalRecords} entries
                      </p>
                    </div>
                  </div>
                  <div className="level-right">
                    <div className="level-item">
                      <div className="field is-grouped">
                        <div className="control">
                          <div className="select">
                            <select
                              value={perPage}
                              onChange={(e) => onPerPageChange(parseInt(e.target.value))}
                            >
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                          </div>
                        </div>
                        <div className="control">
                          <span className="tag is-light">per page</span>
                        </div>
                      </div>
                    </div>
                    {totalPages > 1 && (
                      <div className="level-item">
                        <nav className="pagination is-small" role="navigation">
                          <ul className="pagination-list">
                            {/* Previous arrow */}
                            <li>
                              <button
                                className="pagination-link"
                                onClick={() => onPageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                title="Previous page"
                              >
                                <span className="icon is-small">
                                  <i className="bi bi-chevron-left"></i>
                                </span>
                              </button>
                            </li>

                            {/* Page numbers (max 5, current in middle) */}
                            {(() => {
                              const pages = [];
                              let startPage = Math.max(1, currentPage - 2);
                              let endPage = Math.min(totalPages, startPage + 4);

                              // Adjust start if we're near the end
                              if (endPage - startPage < 4) {
                                startPage = Math.max(1, endPage - 4);
                              }

                              for (let i = startPage; i <= endPage; i++) {
                                pages.push(
                                  <li key={i}>
                                    <button
                                      className={`pagination-link ${i === currentPage ? 'is-current' : ''}`}
                                      onClick={() => onPageChange(i)}
                                    >
                                      {i}
                                    </button>
                                  </li>
                                );
                              }
                              return pages;
                            })()}

                            {/* Next arrow */}
                            <li>
                              <button
                                className="pagination-link"
                                onClick={() => onPageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                title="Next page"
                              >
                                <span className="icon is-small">
                                  <i className="bi bi-chevron-right"></i>
                                </span>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            {!dataLoading && !dataError && tableData.length === 0 && (
              <div className="notification is-warning is-light">
                No data found in table "{selectedTable}"
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DataTable;